// ============================================
// BillingDashboard.jsx - Uses SubscriptionContext
// No duplicate API calls
// ============================================
import React, { useState } from "react";
import { useSubscription } from "../../context/SubscriptionContext";
import StartTrialButton from "./StartTrialButton";
import { COLORS } from '../../utils/colors';

export default function BillingDashboard({ userId }) {
  const { 
    subscriptionData, 
    loading, 
    hasActiveSubscription,
    isTrialing,
    isPastDue,
    trialDaysRemaining,
    refresh 
  } = useSubscription();
  
  const [actionLoading, setActionLoading] = useState(false);

  // Check if subscriptions are enforced
  const enforceSubscriptions = process.env.REACT_APP_ENFORCE_SUBSCRIPTIONS === 'true';

  // If subscriptions are not enforced, show free mode message
  if (!enforceSubscriptions) {
    return (
      <div style={{maxWidth: "600px", margin: "0 auto", padding: "20px"}}>
        <h2 style={{color: COLORS.text}}>BaoBrain Access</h2>
        
        <div style={{
          background: `${COLORS.secondary}15`,
          border: `1px solid ${COLORS.secondary}`,
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "20px",
          textAlign: "center"
        }}>
          <div style={{fontSize: "24px", marginBottom: "12px"}}>🎉</div>
          <h3 style={{color: COLORS.secondary, margin: "0 0 12px 0"}}>
            BaoBrain is Currently Free!
          </h3>
          <p style={{margin: "0", color: COLORS.text, lineHeight: "1.5"}}>
            We're offering BaoBrain for free to all users during our beta period. 
            Enjoy full access to all features with no subscription required.
          </p>
        </div>

        <div style={{
          background: COLORS.background,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          padding: "16px",
          fontSize: "14px",
          color: COLORS.textLight
        }}>
          <h4 style={{margin: "0 0 8px 0", color: COLORS.text}}>What's included:</h4>
          <ul style={{margin: "0", paddingLeft: "20px"}}>
            <li>Visitor heatmaps</li>
            <li>Full analytics dashboard</li>
            <li>AI-powered insights</li>
            <li>All tracking features</li>
          </ul>
        </div>
      </div>
    );
  }

  const openCustomerPortal = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/billing/create-portal-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        console.error("Portal error details:", errorData);
        alert(`Error: ${errorData.detail || 'Failed to open billing portal'}`);
        return;
      }
      
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Portal error:", err);
      alert("Failed to open billing portal. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div>Loading billing information...</div>;
  }

  if (!subscriptionData) {
    return (
      <div>
        <p>Error loading billing information.</p>
        <button onClick={refresh} disabled={loading}>
          {loading ? "Retrying..." : "Retry Load"}
        </button>
      </div>
    );
  }

  // Determine normalized status
  const getNormalizedStatus = () => {
    if (isTrialing) return "trialing";
    if (hasActiveSubscription) return "active";
    if (isPastDue) return "past_due";
    return subscriptionData?.status || "none";
  };

  const normalizedStatus = getNormalizedStatus();

  const renderSubscriptionStatus = () => {
    // Trialing
    if (normalizedStatus === "trialing") {
      return (
        <div style={{
          background: `${COLORS.secondary}15`,
          border: `1px solid ${COLORS.secondary}`,
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "20px"
        }}>
          <h3 style={{color: COLORS.secondary, margin: "0 0 8px 0"}}>
            Free Trial Active
          </h3>
          <p style={{margin: "0", color: COLORS.text}}>
            You have <strong>{trialDaysRemaining}</strong> days left in your free trial.
          </p>
        </div>
      );
    }

    // Active
    if (normalizedStatus === "active") {
      return (
        <div style={{
          background: `${COLORS.primary}15`,
          border: `1px solid ${COLORS.primary}`,
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "20px"
        }}>
          <h3 style={{color: COLORS.primary, margin: "0 0 8px 0"}}>
            ⭐ Subscription Active
          </h3>
          <p style={{margin: "0", color: COLORS.text}}>
            Your subscription is active and up to date.
          </p>
        </div>
      );
    }

    // Past due
    if (normalizedStatus === "past_due") {
      return (
        <div style={{
          background: `${COLORS.primaryDark}15`,
          border: `1px solid ${COLORS.primaryDark}`,
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "20px"
        }}>
          <h3 style={{color: COLORS.primaryDark, margin: "0 0 8px 0"}}>
            ⚠️ Payment Required
          </h3>
          <p style={{margin: "0", color: COLORS.text}}>
            Please update your payment method to continue service.
          </p>
        </div>
      );
    }

    // Cancelled
    if (normalizedStatus === "cancelled") {
      return (
        <div style={{
          background: `${COLORS.primaryDark}20`,
          border: `1px solid ${COLORS.primaryDark}`,
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "20px"
        }}>
          <h3 style={{color: COLORS.primaryDark, margin: "0 0 8px 0"}}>
            🚫 Subscription Cancelled
          </h3>
          <p style={{margin: "0", color: COLORS.text}}>
            Your subscription has been cancelled.
          </p>
        </div>
      );
    }

    // No subscription
    return (
      <div style={{
        background: COLORS.background,
        border: "1px solid #ddd",
        borderRadius: "8px",
        padding: "16px",
        marginBottom: "20px"
      }}>
        <h3 style={{margin: "0 0 8px 0", color: COLORS.text}}>No Active Subscription</h3>
        <p style={{margin: "0", color: COLORS.textLight}}>
          Start your free trial to access all features.
        </p>
      </div>
    );
  };

  const hasRealSubscription = hasActiveSubscription || isTrialing || isPastDue;

  return (
    <div style={{maxWidth: "600px", margin: "0 auto", padding: "20px"}}>
      <h2 style={{color: COLORS.text}}>Current subscription status</h2>
      
      {renderSubscriptionStatus()}

      <div style={{marginBottom: "20px", display: 'flex', alignItems: 'center', gap: '15px'}}>
        {hasRealSubscription ? (
          <>
            <button
              onClick={openCustomerPortal}
              disabled={actionLoading}
              style={{
                backgroundColor: COLORS.secondary,
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "5px",
                cursor: actionLoading ? "not-allowed" : "pointer",
                opacity: actionLoading ? 0.7 : 1,
              }}
            >
              {actionLoading ? "Loading Portal..." : "Manage Subscription"}
            </button>
            <small style={{color: COLORS.textLight}}>
              Update payment method, view invoices, or cancel subscription.
            </small>
          </>
        ) : (
          <StartTrialButton userId={userId} onTrialStart={refresh} />
        )}
      </div>

      {subscriptionData.current_period_end && normalizedStatus !== "cancelled" && (
        <div style={{fontSize: "14px", color: COLORS.textLight, borderTop: "1px solid #eee", paddingTop: "10px"}}>
          <p style={{margin: 0}}>
            Current period ends: <strong>{new Date(subscriptionData.current_period_end).toLocaleDateString()}</strong>
          </p>
        </div>
      )}
    </div>
  );
}